import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useSearchParams } from 'react-router-dom';
// @ts-ignore
import operationMd from '../../portal/YonCodeDocs/operation.md';
// @ts-ignore
import agentMd from '../../portal/YonCodeAgent2Docs/docs.md';
import './docs.scss';
const DOC_CONFIG = {
    yoncode: { md: operationMd, imgBase: './portal/YonCodeDocs' },
    agent: { md: agentMd, imgBase: './portal/YonCodeAgent2Docs' },
};
const DOC_TABS = [
    { key: 'yoncode', label: 'YonCode IDE' },
    { key: 'agent', label: 'YonCode Agent' },
];
function resolveDefaultTab(searchParams) {
    // 外部系统（IDE/工作台）使用的格式：#/docs?agent=yoncode2.0
    const agent = searchParams.get('agent');
    if (agent === 'yoncode2.0')
        return 'agent';
    // 兼容此前的格式：#/docs?productVersion=2
    const productVersion = searchParams.get('productVersion');
    if (productVersion === '2')
        return 'agent';
    return 'yoncode';
}
const HEADING_RE = /^(#{1,6})\s+(.+)$/gm;
function stripMarkdown(text) {
    return text
        .replace(/`([^`]+)`/g, '$1')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
        .replace(/[*_~]/g, '')
        .trim();
}
function slugify(text) {
    return stripMarkdown(text)
        .toLowerCase()
        .replace(/[^\w\u4e00-\u9fa5\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}
function createHeadingId(text) {
    return slugify(text) || 'section';
}
function getNodeText(node) {
    if (node == null || typeof node === 'boolean')
        return '';
    if (typeof node === 'string' || typeof node === 'number')
        return String(node);
    if (Array.isArray(node))
        return node.map(getNodeText).join('');
    if (React.isValidElement(node))
        return getNodeText(node.props.children);
    return '';
}
function buildToc(markdown) {
    const items = [];
    const usedIds = new Map();
    for (const match of markdown.matchAll(HEADING_RE)) {
        const level = match[1].length;
        if (level > 4)
            continue;
        const text = stripMarkdown(match[2]);
        const baseId = createHeadingId(text);
        const count = usedIds.get(baseId) ?? 0;
        usedIds.set(baseId, count + 1);
        const id = count === 0 ? baseId : `${baseId}-${count + 1}`;
        const capturedId = id;
        items.push({
            id,
            text,
            level,
            getId: () => capturedId,
        });
    }
    return items;
}
const TOC_MAP = {
    yoncode: buildToc(operationMd),
    agent: buildToc(agentMd),
};
function createComponents(imgBase, toc) {
    let headingIdx = 0;
    const heading = (Tag) => ({ children, node: _node, ...props }) => {
        const idx = headingIdx++;
        const id = toc[idx]?.getId() ?? 'section';
        return _jsx(Tag, { ...props, id: id, children: children });
    };
    return {
        img: ({ src, alt, ...props }) => {
            const resolvedSrc = src && !src.startsWith('http') && !src.startsWith('/')
                ? `${imgBase}/${src}`
                : src;
            return _jsx("img", { src: resolvedSrc, alt: alt, ...props, style: { maxWidth: '100%' } });
        },
        h1: heading('h1'),
        h2: heading('h2'),
        h3: heading('h3'),
        h4: heading('h4'),
    };
}
export default function DocsPage() {
    const [searchParams] = useSearchParams();
    const defaultTab = useMemo(() => resolveDefaultTab(searchParams), [searchParams]);
    const [activeTab, setActiveTab] = useState(defaultTab);
    const { md, imgBase } = DOC_CONFIG[activeTab];
    const toc = TOC_MAP[activeTab];
    const components = createComponents(imgBase, toc);
    const handleTocClick = (event, id) => {
        event.preventDefault();
        const target = document.getElementById(id);
        if (!target)
            return;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    return (_jsxs("div", { className: "docs-page", children: [_jsx("aside", { className: "docs-sidebar", "aria-label": "\u6587\u6863\u76EE\u5F55", children: _jsxs("div", { className: "docs-sidebar-card", children: [_jsx("select", { className: "docs-select", value: activeTab, onChange: (e) => setActiveTab(e.target.value), children: DOC_TABS.map((tab) => (_jsx("option", { value: tab.key, children: tab.label }, tab.key))) }), _jsx("div", { className: "docs-sidebar-title", children: "\u76EE\u5F55" }), _jsx("nav", { className: "docs-toc", children: toc.map((item) => (_jsx("a", { className: `docs-toc-item docs-toc-level-${item.level}`, href: `#${item.id}`, onClick: (event) => handleTocClick(event, item.id), children: _jsx("span", { children: item.text }) }, `${item.id}-${item.text}`))) })] }) }), _jsx("main", { className: "docs-body", children: _jsx("div", { className: "docs-content", children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: components, children: md }) }) })] }));
}
